import { Badge, Box, HStack, IconButton, Stack, Table, Text, Wrap } from '@chakra-ui/react';
import {
  ArrowPathIcon,
  ClockIcon,
  EllipsisHorizontalIcon,
  PencilSquareIcon,
  TrashIcon,
} from '@heroicons/react/24/outline';
import { ApiKey } from '@/lib/types';
import { describeScope } from '@/lib/api-key-scopes';
import {
  MenuContent,
  MenuItem,
  MenuRoot,
  MenuSeparator,
  MenuTrigger,
} from '@/components/ui/menu';

interface Props {
  apiKeys: ApiKey[];
  onRename: (key: ApiKey) => void;
  onRotate: (key: ApiKey) => void;
  onViewAudit: (key: ApiKey) => void;
  onRevoke: (key: ApiKey) => void;
}

function formatDate(value?: string | null) {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleString();
}

function statusBadge(key: ApiKey) {
  if (key.revoked_at) {
    return (
      <Badge variant='danger'>
        Revoked
      </Badge>
    );
  }
  if (key.expires_at && new Date(key.expires_at).getTime() < Date.now()) {
    return (
      <Badge>
        Expired
      </Badge>
    );
  }
  return (
    <Badge variant='success'>
      Active
    </Badge>
  );
}

function expiresCell(key: ApiKey) {
  if (!key.expires_at) {
    return (
      <Text color='fg.muted' fontSize='sm'>
        Never
      </Text>
    );
  }
  const t = new Date(key.expires_at).getTime();
  if (Number.isNaN(t)) return <Text color='fg.muted'>—</Text>;
  const diffMs = t - Date.now();
  const diffDays = Math.ceil(diffMs / (1000 * 60 * 60 * 24));
  if (diffDays < 0) {
    return (
      <Text color='fg.muted' fontSize='sm'>
        Expired
      </Text>
    );
  }
  const urgent = diffDays <= 7;
  return (
    <Stack gap={0}>
      <Text fontSize='sm' color={urgent ? 'danger' : undefined}>
        in {diffDays} day{diffDays === 1 ? '' : 's'}
      </Text>
      <Text fontSize='xs' color='fg.muted'>
        {new Date(key.expires_at).toLocaleDateString()}
      </Text>
    </Stack>
  );
}

export default function ApiKeysList({
  apiKeys,
  onRename,
  onRotate,
  onViewAudit,
  onRevoke,
}: Props) {
  if (apiKeys.length === 0) {
    return (
      <Box py={10} textAlign='center'>
        <Text color='fg.muted'>You haven&apos;t created any API keys yet.</Text>
      </Box>
    );
  }

  return (
    <Table.Root size='md' variant='outline'>
      <Table.Header>
        <Table.Row>
          <Table.ColumnHeader>Name</Table.ColumnHeader>
          <Table.ColumnHeader>Scopes</Table.ColumnHeader>
          <Table.ColumnHeader>Status</Table.ColumnHeader>
          <Table.ColumnHeader>Expires</Table.ColumnHeader>
          <Table.ColumnHeader>Last used</Table.ColumnHeader>
          <Table.ColumnHeader>Created</Table.ColumnHeader>
          <Table.ColumnHeader textAlign='end'>Actions</Table.ColumnHeader>
        </Table.Row>
      </Table.Header>
      <Table.Body>
        {apiKeys.map((key) => {
          const isRevoked = Boolean(key.revoked_at);
          return (
            <Table.Row key={key.id}>
              <Table.Cell>
                <Stack gap={0}>
                  <Text fontWeight='medium'>{key.name}</Text>
                  <Text fontFamily='mono' fontSize='xs' color='fg.muted'>
                    {key.key_prefix}…
                  </Text>
                  {key.created_by_name && (
                    <Text fontSize='xs' color='fg.muted'>
                      by {key.created_by_name}
                    </Text>
                  )}
                </Stack>
              </Table.Cell>
              <Table.Cell>
                <Wrap gap={1}>
                  {(key.scopes ?? []).map((scope) => (
                    <Badge
                      key={scope}
                      variant='outline'
                      size='sm'
                      title={describeScope(scope)}
                    >
                      {scope}
                    </Badge>
                  ))}
                </Wrap>
              </Table.Cell>
              <Table.Cell>{statusBadge(key)}</Table.Cell>
              <Table.Cell>{expiresCell(key)}</Table.Cell>
              <Table.Cell>
                <Stack gap={0}>
                  <Text fontSize='sm'>{formatDate(key.last_used_at)}</Text>
                  {key.last_used_ip && (
                    <Text fontSize='xs' color='fg.muted' fontFamily='mono'>
                      {key.last_used_ip}
                    </Text>
                  )}
                </Stack>
              </Table.Cell>
              <Table.Cell>
                <Text fontSize='sm'>{formatDate(key.created_at)}</Text>
              </Table.Cell>
              <Table.Cell textAlign='end'>
                <HStack justify='flex-end'>
                  <MenuRoot>
                    <MenuTrigger asChild>
                      <IconButton
                        aria-label='Open actions menu'
                        size='xs'
                        variant='ghost'
                      >
                        <EllipsisHorizontalIcon width={16} height={16} />
                      </IconButton>
                    </MenuTrigger>
                    <MenuContent>
                      <MenuItem
                        value='rename'
                        disabled={isRevoked}
                        onClick={() => onRename(key)}
                      >
                        <PencilSquareIcon width={14} height={14} />
                        <Box ml={2}>Rename</Box>
                      </MenuItem>
                      <MenuItem
                        value='rotate'
                        disabled={isRevoked}
                        onClick={() => onRotate(key)}
                      >
                        <ArrowPathIcon width={14} height={14} />
                        <Box ml={2}>Rotate</Box>
                      </MenuItem>
                      <MenuItem value='audit' onClick={() => onViewAudit(key)}>
                        <ClockIcon width={14} height={14} />
                        <Box ml={2}>View audit log</Box>
                      </MenuItem>
                      <MenuSeparator />
                      <MenuItem
                        value='revoke'
                        color='danger'
                        disabled={isRevoked}
                        onClick={() => onRevoke(key)}
                      >
                        <TrashIcon width={14} height={14} />
                        <Box ml={2}>Revoke</Box>
                      </MenuItem>
                    </MenuContent>
                  </MenuRoot>
                </HStack>
              </Table.Cell>
            </Table.Row>
          );
        })}
      </Table.Body>
    </Table.Root>
  );
}
